import { useState } from 'react'
import { LuMic } from 'react-icons/lu'
import { useSpeechToText } from '../../hooks/useSpeechToText'
import { useToast } from '../../context/ToastContext'
import LanguageSelect from './LanguageSelect'

// Remember the astrologer's dictation language across both notes editors and
// future sessions — the two editors never mount together, so localStorage is
// what carries the choice between them.
const LANG_KEY = 'astro-dictation-lang'

function readStoredLang() {
  try {
    return localStorage.getItem(LANG_KEY) || 'en-IN'
  } catch {
    return 'en-IN'
  }
}

/** Web Speech error code → what to tell the astrologer. */
export function describeDictationError(code) {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Microphone access was blocked — allow it in your browser (lock icon in the address bar) to dictate notes.'
    case 'audio-capture':
      return 'Voice typing could not reach your microphone — it may be in use or disconnected. Check the device, then tap the mic again.'
    case 'network':
      return 'Voice typing lost its connection to the speech service. Check your internet connection and tap the mic again.'
    case 'language-not-supported':
      return 'Your browser cannot recognise speech in that language — pick another language to dictate.'
    default:
      return 'Voice typing stopped unexpectedly. Please tap the mic to try again.'
  }
}

/**
 * Voice-to-text control for a notes field: a language picker plus a mic toggle.
 * Wraps useSpeechToText — each settled phrase is pushed to `onTranscript` for
 * the parent to append to its value, and the phrase still forming shows as a
 * live caption beside the mic.
 *
 * Renders nothing when the browser lacks Speech Recognition (e.g. Firefox), so
 * callers need no capability check of their own. The gold-outline idle / red-
 * pulse recording styling reads clearly on both the light appointment page and
 * the dark in-call console, so there's no theme prop to pass.
 */
export default function DictationButton({ onTranscript, disabled = false }) {
  const { showToast } = useToast()
  const [lang, setLang] = useState(readStoredLang)

  const { supported, listening, interim, toggle } = useSpeechToText({
    lang,
    onResult: onTranscript,
    // The hook already retries the transient engine errors (speech-service
    // hiccups, a briefly busy microphone — common while a call holds the mic);
    // what reaches here is final, so say what actually went wrong.
    onError: (code) => showToast(describeDictationError(code)),
  })

  if (!supported) return null

  const changeLang = (value) => {
    setLang(value)
    try {
      localStorage.setItem(LANG_KEY, value)
    } catch {
      /* storage unavailable (private mode) — the in-memory choice still works */
    }
  }

  return (
    <div className="flex items-center gap-2 min-w-0">
      {listening && (
        <span className="min-w-0 max-w-[45vw] sm:max-w-[160px] truncate text-[10px] italic text-neutral-400">
          {interim || 'Listening…'}
        </span>
      )}
      {/* Language can't change mid-dictation — the engine listens in one
          language per session, so lock the picker while recording. */}
      <LanguageSelect value={lang} onChange={changeLang} disabled={disabled || listening} />
      <button
        type="button"
        onClick={toggle}
        disabled={disabled}
        aria-pressed={listening}
        title={listening ? 'Stop voice typing' : 'Dictate the note with your voice'}
        aria-label={listening ? 'Stop voice typing' : 'Start voice typing'}
        className={`relative shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
          listening
            ? 'bg-red-500/15 border-red-400/50 text-red-500'
            : 'bg-transparent border-[#C8922A]/40 text-[#C8922A] hover:bg-[#C8922A]/10'
        }`}
      >
        <LuMic className="w-4 h-4" />
        {listening && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-red-500 animate-pulse" />}
      </button>
    </div>
  )
}
